import { env } from '../env';
import { supabase } from './supabase';

/** 통일 에러 모델 — API 실패 응답 { error: { code, message, details } } 파서 */
export class ApiError extends Error {
  constructor(
    public readonly code: string,
    public readonly status: number,
    message: string,
    public readonly details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

interface ApiErrorBody {
  error?: { code?: string; message?: string; details?: unknown };
}

export interface ApiFetchOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  /** JSON 바디 — 지정 시 Content-Type 자동 설정 */
  json?: unknown;
  /** 쿼리스트링 — undefined / 빈 문자열 값은 생략 */
  query?: Record<string, string | number | boolean | undefined>;
  signal?: AbortSignal;
}

/**
 * 어드민 전용 얇은 fetch 래퍼.
 * hono RPC 대신 계약 기반 호출 — 어드민 라우트 타입 미노출 가능성 대비.
 * Authorization 헤더(Supabase 세션 access token)를 자동 첨부한다.
 */
export async function apiFetch<T>(path: string, options: ApiFetchOptions = {}): Promise<T> {
  const { method = 'GET', json, query, signal } = options;

  const url = new URL(path, env.VITE_API_URL);
  if (query) {
    for (const [key, value] of Object.entries(query)) {
      if (value !== undefined && value !== '') url.searchParams.set(key, String(value));
    }
  }

  const headers: Record<string, string> = {};
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  if (token) headers.Authorization = `Bearer ${token}`;
  if (json !== undefined) headers['Content-Type'] = 'application/json';

  const res = await fetch(url.toString(), {
    method,
    headers,
    body: json !== undefined ? JSON.stringify(json) : undefined,
    signal,
  });

  if (!res.ok) {
    let body: ApiErrorBody | null = null;
    try {
      body = (await res.json()) as ApiErrorBody;
    } catch {
      // 본문이 JSON이 아니면 상태 코드만으로 에러 구성
    }
    throw new ApiError(
      body?.error?.code ?? 'UNKNOWN',
      res.status,
      body?.error?.message ?? `HTTP ${res.status}`,
      body?.error?.details,
    );
  }

  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}
